import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  MessageSquare,
  LogOut,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Filter
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/bookings`, { credentials: 'include' });
      const data = await response.json();
      setBookings(data);
      setFilteredBookings(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching bookings:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (filter === 'all') {
      setFilteredBookings(bookings);
    } else {
      setFilteredBookings(bookings.filter(b => b.booking_status === filter));
    }
  }, [filter, bookings]);

  const updateBookingStatus = useCallback(async (bookingId, newStatus) => {
    try {
      await fetch(`${API}/admin/bookings/${bookingId}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_status: newStatus })
      });
      fetchBookings();
      setSelectedBooking(null);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating status:', error);
      }
    }
  }, [fetchBookings]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div data-testid="admin-bookings-page" className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <div className="bg-[#1A1A1A] text-[#FAF8F5] py-4 px-6 md:px-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-[#C9A84C]">
              De Azraa
            </h1>
            <span className="text-xl font-medium">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm">{user?.email}</span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm hover:text-[#C9A84C] transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-[#E5E5E5] px-6 md:px-12 py-4">
        <nav className="flex gap-8">
          <Link
            to="/admin/dashboard"
            data-testid="nav-dashboard"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <LayoutDashboard size={16} />
            Dashboard
          </Link>
          <Link
            to="/admin/bookings"
            data-testid="nav-bookings"
            className="flex items-center gap-2 text-sm font-medium text-[#C9A84C] border-b-2 border-[#C9A84C] pb-2"
          >
            <Calendar size={16} />
            Bookings
          </Link>
          <Link
            to="/admin/suites"
            data-testid="nav-suites"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Suites
          </Link>
          <Link
            to="/admin/contacts"
            data-testid="nav-contacts"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <MessageSquare size={16} />
            Contact Messages
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="px-6 md:px-12 py-12">
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A]">
              All Bookings
            </h2>
            
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-[#3A3A3A]" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="status-filter"
                className="border border-[#E5E5E5] px-4 py-2 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C]"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {loading ? (
            <p className="text-center py-8 text-[#3A3A3A]">Loading bookings...</p>
          ) : filteredBookings.length === 0 ? (
            <p className="text-center py-8 text-[#3A3A3A]" data-testid="no-bookings">No bookings found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="bookings-table">
                <thead>
                  <tr className="border-b border-[#E5E5E5]">
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Guest</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Contact</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Suite</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Check-in</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Nights</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Amount</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Status</th>
                    <th className="text-left py-3 text-xs uppercase tracking-wider text-[#3A3A3A]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr
                      key={booking.id}
                      data-testid={`booking-row-${booking.id}`}
                      className="border-b border-[#E5E5E5] hover:bg-[#FAF8F5] transition-colors"
                    >
                      <td className="py-4 text-sm text-[#1A1A1A]">{booking.guest_name}</td>
                      <td className="py-4 text-sm text-[#3A3A3A]">
                        <div>{booking.guest_email}</div>
                        <div>{booking.guest_phone}</div>
                      </td>
                      <td className="py-4 text-sm text-[#3A3A3A]">{booking.suite_name}</td>
                      <td className="py-4 text-sm text-[#3A3A3A]">
                        {new Date(booking.check_in).toLocaleDateString()}
                      </td>
                      <td className="py-4 text-sm text-[#3A3A3A]">{booking.num_nights}</td>
                      <td className="py-4 text-sm text-[#1A1A1A] font-medium">
                        ₦{booking.total_amount.toLocaleString()}
                      </td>
                      <td className="py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium ${
                            booking.booking_status === 'confirmed'
                              ? 'bg-green-50 text-green-800'
                              : booking.booking_status === 'pending'
                              ? 'bg-yellow-50 text-yellow-800'
                              : 'bg-red-50 text-red-800'
                          }`}
                        >
                          {booking.booking_status === 'confirmed' && <CheckCircle size={12} />}
                          {booking.booking_status === 'pending' && <Clock size={12} />}
                          {booking.booking_status === 'cancelled' && <XCircle size={12} />}
                          {booking.booking_status}
                        </span>
                      </td>
                      <td className="py-4">
                        <button
                          onClick={() => setSelectedBooking(booking)}
                          data-testid={`view-booking-${booking.id}`}
                          className="text-[#C9A84C] hover:text-[#1A1A1A] transition-colors"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div
          data-testid="booking-modal"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6"
          onClick={() => setSelectedBooking(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <h3 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A] mb-6">
              Booking Details
            </h3>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Guest Name</p>
                <p className="text-[#1A1A1A]">{selectedBooking.guest_name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Contact</p>
                <p className="text-[#1A1A1A]">{selectedBooking.guest_email}</p>
                <p className="text-[#1A1A1A]">{selectedBooking.guest_phone}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Suite</p>
                <p className="text-[#1A1A1A]">{selectedBooking.suite_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Check-in</p>
                  <p className="text-[#1A1A1A]">{new Date(selectedBooking.check_in).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Check-out</p>
                  <p className="text-[#1A1A1A]">{new Date(selectedBooking.check_out).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Guests</p>
                  <p className="text-[#1A1A1A]">{selectedBooking.num_guests}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Nights</p>
                  <p className="text-[#1A1A1A]">{selectedBooking.num_nights}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Total Amount</p>
                <p className="text-xl font-serif text-[#1A1A1A]">₦{selectedBooking.total_amount.toLocaleString()}</p>
              </div>
              {selectedBooking.special_requests && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Special Requests</p>
                  <p className="text-[#1A1A1A]">{selectedBooking.special_requests}</p>
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Payment Status</p>
                <p className="text-[#1A1A1A]">{selectedBooking.payment_status}</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-3">Update Status</p>
              <div className="flex gap-3">
                <button
                  onClick={() => updateBookingStatus(selectedBooking.id, 'pending')}
                  data-testid="status-pending-btn"
                  className="px-4 py-2 bg-yellow-100 text-yellow-800 text-sm hover:bg-yellow-200 transition-colors"
                >
                  Pending
                </button>
                <button
                  onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
                  data-testid="status-confirmed-btn"
                  className="px-4 py-2 bg-green-100 text-green-800 text-sm hover:bg-green-200 transition-colors"
                >
                  Confirmed
                </button>
                <button
                  onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                  data-testid="status-cancelled-btn"
                  className="px-4 py-2 bg-red-100 text-red-800 text-sm hover:bg-red-200 transition-colors"
                >
                  Cancelled
                </button>
              </div>
            </div>

            <button
              onClick={() => setSelectedBooking(null)}
              data-testid="close-modal-btn"
              className="w-full border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors px-8 py-3 text-sm tracking-widest uppercase"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;